import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Stack,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    Category as CategoryIcon,
    Task as TaskIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { ChecklistCategory, ChecklistItem, Program } from '@modules/programManager/types';
import { apiService } from '@modules/programManager/services/apiService';

const ChecklistManagement: React.FC = () => {
    const [categories, setCategories] = useState<ChecklistCategory[]>([]);
    const [programs, setPrograms] = useState<Program[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dialog states
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [itemDialogOpen, setItemDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Editing states
    const [editingCategory, setEditingCategory] = useState<ChecklistCategory | null>(null);
    const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);
    const [deletingItem, setDeletingItem] = useState<{ type: 'category' | 'item'; id: string; name: string } | null>(null);

    // Form data
    const [categoryFormData, setCategoryFormData] = useState({
        name: '',
        description: '',
        order: 0,
    });

    const [itemFormData, setItemFormData] = useState({
        programId: '',
        categoryId: '',
        title: '',
        description: '',
        status: 'pending' as 'pending' | 'in-progress' | 'completed' | 'not_applicable',
        order: 0,
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const [categoriesData, programsData] = await Promise.all([
                apiService.getChecklistCategories(),
                apiService.getPrograms(),
            ]);

            setCategories(categoriesData);
            setPrograms(programsData);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddCategory = () => {
        setEditingCategory(null);
        setCategoryFormData({ name: '', description: '', order: categories.length });
        setCategoryDialogOpen(true);
    };

    const handleEditCategory = (category: ChecklistCategory) => {
        setEditingCategory(category);
        setCategoryFormData({
            name: category.title,
            description: category.description || '',
            order: category.order || 0,
        });
        setCategoryDialogOpen(true);
    };

    const handleSaveCategory = async () => {
        try {
            if (editingCategory) {
                const updatedCategory = await apiService.updateChecklistCategory(editingCategory.type_id.toString(), categoryFormData);
                setCategories(categories.map(c => c.type_id === editingCategory.type_id ? updatedCategory : c));
            } else {
                const newCategory = await apiService.createChecklistCategory(categoryFormData);
                setCategories([...categories, newCategory]);
            }
            setCategoryDialogOpen(false);
            setEditingCategory(null);
        } catch (error) {
            console.error('Error saving category:', error);
            setError('Failed to save category. Please try again.');
        }
    };

    const handleDeleteCategory = (category: ChecklistCategory) => {
        setDeletingItem({ type: 'category', id: category.type_id.toString(), name: category.title });
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deletingItem) return;

        try {
            if (deletingItem.type === 'category') {
                await apiService.deleteChecklistCategory(deletingItem.id);
                setCategories(categories.filter(c => c.type_id.toString() !== deletingItem.id));
            }
            setDeleteDialogOpen(false);
            setDeletingItem(null);
        } catch (error) {
            console.error('Error deleting item:', error);
            setError('Failed to delete item. Please try again.');
        }
    };

    const handleAddItem = (categoryId: string) => {
        setEditingItem(null);
        setItemFormData({
            programId: programs[0]?.id || '',
            categoryId: categoryId,
            title: '',
            description: '',
            status: 'pending',
            order: 0,
        });
        setItemDialogOpen(true);
    };

    const handleEditItem = (item: ChecklistItem) => {
        setEditingItem(item);
        setItemFormData({
            programId: item.programId,
            categoryId: item.categoryId,
            title: item.title,
            description: item.description || '',
            status: item.status,
            order: item.order,
        });
        setItemDialogOpen(true);
    };

    const handleSaveItem = async () => {
        try {
            if (editingItem) {
                const updatedItem = await apiService.updateChecklistItem(
                    itemFormData.programId,
                    editingItem.id,
                    {
                        ...itemFormData,
                        isCompleted: itemFormData.status === 'completed'
                    }
                );
                // Update local state if needed
            } else {
                const newItem = await apiService.createChecklistItem(
                    itemFormData.programId,
                    {
                        ...itemFormData,
                        isCompleted: itemFormData.status === 'completed'
                    }
                );
                // Update local state if needed
            }
            setItemDialogOpen(false);
            setEditingItem(null);
        } catch (error) {
            console.error('Error saving item:', error);
            setError('Failed to save item. Please try again.');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{
            p: {
                xs: 2,
                md: 3,
                '@media (min-width: 896px)': 3.5,
                '@media (min-width: 1356px)': 4,
                lg: 4
            }
        }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={{
                xs: 2,
                md: 3,
                '@media (min-width: 896px)': 3.5,
                '@media (min-width: 1356px)': 4,
                lg: 4
            }}>
                <Typography variant="h4" component="h1" sx={{
                    color: 'white',
                    fontSize: {
                        xs: '1.75rem',
                        md: '2.125rem',
                        '@media (min-width: 896px)': '2.25rem',
                        '@media (min-width: 1356px)': '2.5rem',
                        lg: '2.5rem'
                    }
                }}>
                    Checklist Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCategory}
                    sx={{
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        '&:hover': {
                            background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)',
                        },
                        fontSize: {
                            xs: '0.875rem',
                            md: '0.875rem',
                            '@media (min-width: 896px)': '0.9rem',
                            '@media (min-width: 1356px)': '1rem',
                            lg: '1rem'
                        },
                        padding: {
                            xs: '8px 16px',
                            md: '8px 16px',
                            '@media (min-width: 896px)': '10px 20px',
                            '@media (min-width: 1356px)': '12px 24px',
                            lg: '12px 24px'
                        }
                    }}
                >
                    Add Category
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Categories List */}
            {categories.map((category) => (
                <Accordion key={category.type_id} sx={{
                    mb: {
                        xs: 1.5,
                        md: 2,
                        '@media (min-width: 896px)': 2.5,
                        '@media (min-width: 1356px)': 3,
                        lg: 3
                    },
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: {
                        xs: 1,
                        md: 1,
                        '@media (min-width: 896px)': 1.5,
                        '@media (min-width: 1356px)': 2,
                        lg: 2
                    }
                }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                        <Box display="flex" alignItems="center" width="100%">
                            <CategoryIcon sx={{
                                color: 'white',
                                mr: {
                                    xs: 1.5,
                                    md: 2,
                                    '@media (min-width: 896px)': 2.5,
                                    '@media (min-width: 1356px)': 3,
                                    lg: 3
                                },
                                fontSize: {
                                    xs: '1.25rem',
                                    md: '1.5rem',
                                    '@media (min-width: 896px)': '1.6rem',
                                    '@media (min-width: 1356px)': '1.75rem',
                                    lg: '1.75rem'
                                }
                            }} />
                            <Typography variant="h6" sx={{
                                color: 'white',
                                fontSize: {
                                    xs: '1.125rem',
                                    md: '1.25rem',
                                    '@media (min-width: 896px)': '1.35rem',
                                    '@media (min-width: 1356px)': '1.5rem',
                                    lg: '1.5rem'
                                }
                            }}>
                                {category.title}
                            </Typography>
                            <Box ml="auto" display="flex" gap={{
                                xs: 0.5,
                                md: 1,
                                '@media (min-width: 896px)': 1.2,
                                '@media (min-width: 1356px)': 1.5,
                                lg: 1.5
                            }}>
                                <Tooltip title="Add Item">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleAddItem(category.type_id.toString());
                                        }}
                                        sx={{
                                            color: '#2196F3',
                                            fontSize: {
                                                xs: '1.25rem',
                                                md: '1.25rem',
                                                '@media (min-width: 896px)': '1.35rem',
                                                '@media (min-width: 1356px)': '1.5rem',
                                                lg: '1.5rem'
                                            }
                                        }}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit Category">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditCategory(category);
                                        }}
                                        sx={{
                                            color: '#FF9800',
                                            fontSize: {
                                                xs: '1.25rem',
                                                md: '1.25rem',
                                                '@media (min-width: 896px)': '1.35rem',
                                                '@media (min-width: 1356px)': '1.5rem',
                                                lg: '1.5rem'
                                            }
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Category">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteCategory(category);
                                        }}
                                        sx={{
                                            color: '#f44336',
                                            fontSize: {
                                                xs: '1.25rem',
                                                md: '1.25rem',
                                                '@media (min-width: 896px)': '1.35rem',
                                                '@media (min-width: 1356px)': '1.5rem',
                                                lg: '1.5rem'
                                            }
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{
                        padding: {
                            xs: '12px 16px',
                            md: '16px 24px',
                            '@media (min-width: 896px)': '20px 28px',
                            '@media (min-width: 1356px)': '24px 32px',
                            lg: '24px 32px'
                        }
                    }}>
                        <Typography variant="body2" sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            mb: {
                                xs: 1.5,
                                md: 2,
                                '@media (min-width: 896px)': 2.2,
                                '@media (min-width: 1356px)': 2.5,
                                lg: 2.5
                            },
                            fontSize: {
                                xs: '0.875rem',
                                md: '0.875rem',
                                '@media (min-width: 896px)': '0.9rem',
                                '@media (min-width: 1356px)': '1rem',
                                lg: '1rem'
                            }
                        }}>
                            {category.description || 'No description provided'}
                        </Typography>
                        <Typography variant="body2" sx={{
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: {
                                xs: '0.75rem',
                                md: '0.75rem',
                                '@media (min-width: 896px)': '0.8rem',
                                '@media (min-width: 1356px)': '0.875rem',
                                lg: '0.875rem'
                            }
                        }}>
                            Order: {category.order}
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            ))}

            {/* Add Category Dialog */}
            <Dialog
                open={categoryDialogOpen}
                onClose={() => setCategoryDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        margin: {
                            xs: 2,
                            md: 3,
                            '@media (min-width: 896px)': 3.5,
                            '@media (min-width: 1356px)': 4,
                            lg: 4
                        },
                        maxWidth: {
                            xs: 'calc(100% - 32px)',
                            md: '600px',
                            '@media (min-width: 896px)': '650px',
                            '@media (min-width: 1356px)': '700px',
                            lg: '700px'
                        }
                    }
                }}
            >
                <DialogTitle>
                    {editingCategory ? 'Edit Category' : 'Add New Category'}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <Box>
                            <TextField
                                fullWidth
                                label="Category Name"
                                value={categoryFormData.name}
                                onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                                required
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={categoryFormData.description}
                                onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Order"
                                type="number"
                                value={categoryFormData.order}
                                onChange={(e) => setCategoryFormData({ ...categoryFormData, order: parseInt(e.target.value) || 0 })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveCategory} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Add Item Dialog */}
            <Dialog
                open={itemDialogOpen}
                onClose={() => setItemDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        margin: {
                            xs: 2,
                            md: 3,
                            '@media (min-width: 896px)': 3.5,
                            '@media (min-width: 1356px)': 4,
                            lg: 4
                        },
                        maxWidth: {
                            xs: 'calc(100% - 32px)',
                            md: '600px',
                            '@media (min-width: 896px)': '650px',
                            '@media (min-width: 1356px)': '700px',
                            lg: '700px'
                        }
                    }
                }}
            >
                <DialogTitle>
                    {editingItem ? 'Edit Item' : 'Add New Item'}
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
                        <Box>
                            <FormControl fullWidth>
                                <InputLabel>Program</InputLabel>
                                <Select
                                    value={itemFormData.programId}
                                    onChange={(e) => setItemFormData({ ...itemFormData, programId: e.target.value })}
                                    label="Program"
                                    required
                                >
                                    {programs.map((program) => (
                                        <MenuItem key={program.id} value={program.id}>
                                            {program.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Item Title"
                                value={itemFormData.title}
                                onChange={(e) => setItemFormData({ ...itemFormData, title: e.target.value })}
                                required
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={itemFormData.description}
                                onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
                            />
                        </Box>
                        <Box>
                            <TextField
                                fullWidth
                                label="Order"
                                type="number"
                                value={itemFormData.order}
                                onChange={(e) => setItemFormData({ ...itemFormData, order: parseInt(e.target.value) || 0 })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setItemDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSaveItem} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete {deletingItem?.type === 'category' ? 'Category' : 'Item'}</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{deletingItem?.name}"? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChecklistManagement;
